import { useState, useEffect } from "react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "../../components/ui/sheet";

export default function DetailDrawer({
    open,
    onOpenChange,
    record,
    fields,
    onSave,
    testIdPrefix,
}) {
    const [notes, setNotes] = useState("");
    const [handled, setHandled] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        if (record) {
            setNotes(record.notes || "");
            setHandled(Boolean(record.handled));
            setError("");
            setSavedAt(null);
        }
    }, [record]);

    if (!record) return null;

    const onClickSave = async () => {
        setSaving(true);
        setError("");
        try {
            await onSave({ handled, notes });
            setSavedAt(new Date());
        } catch (err) {
            setError(err.message || "Failed to save.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="!bg-[#0B0B0C] !text-[#F3EFE7] !border-l-[rgba(201,168,106,0.3)] w-full sm:max-w-xl p-0 flex flex-col"
                data-testid={`${testIdPrefix}-drawer`}
            >
                <SheetHeader className="px-8 py-6 border-b border-[rgba(201,168,106,0.3)] text-left space-y-2 shrink-0 bg-[#15161A]">
                    <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#C9A86A]">
                        Record
                    </span>
                    <SheetTitle className="font-serif text-2xl md:text-3xl text-[#F3EFE7]">
                        {record.full_name || record.email || record.id}
                    </SheetTitle>
                    <SheetDescription className="text-[11px] tracking-[0.22em] uppercase text-[#9A958A]">
                        {record.created_at
                            ? new Date(record.created_at).toLocaleString()
                            : "Record detail"}
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7">
                    <dl className="space-y-5">
                        {fields.map((f) => {
                            const value = record[f.key];
                            if (value === undefined || value === null || value === "")
                                return null;
                            return (
                                <div key={f.key}>
                                    <dt className="text-[10px] tracking-[0.32em] uppercase text-[#9A958A] mb-1.5">
                                        {f.label}
                                    </dt>
                                    <dd
                                        className={`text-[${
                                            f.muted ? "13px" : "15px"
                                        }] leading-[1.7] ${
                                            f.muted
                                                ? "text-[#9A958A] font-mono break-all text-xs"
                                                : "text-[#F3EFE7]"
                                        } ${f.multiline ? "whitespace-pre-wrap" : ""}`}
                                    >
                                        {String(value)}
                                    </dd>
                                </div>
                            );
                        })}
                    </dl>

                    <div className="border-t border-[rgba(201,168,106,0.15)] pt-6">
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={handled}
                                onChange={(e) => setHandled(e.target.checked)}
                                className="w-4 h-4 accent-[#C9A86A] cursor-pointer"
                                data-testid={`${testIdPrefix}-drawer-handled`}
                            />
                            <span className="text-[11px] tracking-[0.28em] uppercase text-[#F3EFE7]">
                                Mark Handled
                            </span>
                        </label>
                    </div>

                    <div>
                        <label
                            htmlFor={`${testIdPrefix}-notes`}
                            className="block text-[10px] tracking-[0.32em] uppercase text-[#9A958A] mb-2"
                        >
                            Internal Notes
                        </label>
                        <textarea
                            id={`${testIdPrefix}-notes`}
                            rows={5}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Internal context — never shown to the client."
                            className="w-full bg-[#15161A] border border-[rgba(201,168,106,0.2)] focus:border-[#C9A86A] outline-none text-[#F3EFE7] text-sm py-3 px-4 transition-colors duration-300 placeholder:text-[#5e5a52] resize-y"
                            data-testid={`${testIdPrefix}-drawer-notes`}
                        />
                    </div>

                    {error && (
                        <div
                            role="alert"
                            className="border border-[#C97A6A]/40 bg-[#C97A6A]/5 px-4 py-3 text-[13px] text-[#E0A89A]"
                            data-testid={`${testIdPrefix}-drawer-error`}
                        >
                            {error}
                        </div>
                    )}
                </div>

                <div className="px-8 py-5 border-t border-[rgba(201,168,106,0.3)] flex items-center justify-between gap-4 bg-[#15161A] shrink-0">
                    {savedAt ? (
                        <span
                            className="text-[11px] tracking-[0.22em] uppercase text-[#C9A86A]"
                            data-testid={`${testIdPrefix}-drawer-saved`}
                        >
                            Saved
                        </span>
                    ) : (
                        <span />
                    )}
                    <button
                        type="button"
                        onClick={onClickSave}
                        disabled={saving}
                        className="btn-gold text-[11px] tracking-[0.22em] uppercase px-6 py-2.5 disabled:opacity-70 disabled:cursor-wait"
                        data-testid={`${testIdPrefix}-drawer-save`}
                    >
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
